class ValidationError extends Error {
    constructor(errors) {
        super("Validation Error");
        this.name = "ValidationError";
        this.errors = errors;
    }
}

class Api {
    constructor() {
        this.baseUrl = "https://gego.ge/gego-api";
        this.token = localStorage.getItem("token");
    }

    get headers() {
        const headers = {
            "Content-Type": "application/json",
        };

        if (this.token) {
            headers["Authorization"] = `Bearer ${this.token}`;
        }

        return headers;
    }

    setToken(token) {
        this.token = token;
        localStorage.setItem("token", token);
    }

    clearToken() {
        this.token = null;
        localStorage.removeItem("token");
    }

    async _handleResponse(response) {
        const contentType = response.headers.get("content-type");

        if (response.status === 204) {
            return null;
        }

        if (response.status === 401) {
            this.clearToken();
            throw new Error("Unauthorized - Please login again");
        }

        if (contentType?.includes("application/json")) {
            const data = await response.json();
            if (!response.ok) {
                const error = new Error(data.detail || "API request failed");
                error.status = response.status;
                error.details = data;
                throw error;
            }
            return data;
        }

        if (contentType?.includes("audio/mpeg")) {
            return response.blob();
        }

        throw new Error(`Unsupported content type: ${contentType}`);
    }

    // Validation methods
    validateEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            return "Please enter a valid email address";
        }
        return null;
    }

    validateUsername(username) {
        if (!username) {
            return "Username is required";
        }
        if (username.length < 3) {
            return "Username must be at least 3 characters long";
        }
        if (username.length > 30) {
            return "Username must be less than 30 characters";
        }
        // Allow only letters, numbers, and underscores
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            return "Username can only contain letters, numbers, and underscores";
        }
        return null;
    }

    validatePassword(password) {
        if (!password) {
            return "Password is required";
        }
        if (password.length < 8) {
            return "Password must be at least 8 characters long";
        }
        if (password.length > 50) {
            return "Password must be less than 50 characters";
        }
        if (!/[A-Z]/.test(password)) {
            return "Password must contain at least one uppercase letter";
        }
        if (!/[a-z]/.test(password)) {
            return "Password must contain at least one lowercase letter";
        }
        if (!/[0-9]/.test(password)) {
            return "Password must contain at least one number";
        }
        return null;
    }

    validateMessage(message) {
        if (!message || message.trim().length === 0) {
            return "Message cannot be empty";
        }
        if (message.length > 1000) {
            return "Message is too long (maximum 1000 characters)";
        }
        return null;
    }

    validateLoginInput(username, password) {
        const errors = {};

        const usernameError = this.validateUsername(username);
        if (usernameError) errors.username = usernameError;

        const passwordError = this.validatePassword(password);
        if (passwordError) errors.password = passwordError;

        return Object.keys(errors).length > 0 ? errors : null;
    }

    validateRegistrationInput(username, email, password) {
        const errors = {};

        const usernameError = this.validateUsername(username);
        if (usernameError) errors.username = usernameError;

        const emailError = this.validateEmail(email);
        if (emailError) errors.email = emailError;

        const passwordError = this.validatePassword(password);
        if (passwordError) errors.password = passwordError;

        return Object.keys(errors).length > 0 ? errors : null;
    }

    // Auth methods
    async login(username, password) {
        try {
            const formData = new URLSearchParams();
            formData.append("username", username);
            formData.append("password", password);

            const response = await fetch(`${this.baseUrl}/api/auth/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: formData,
            });

            const data = await this._handleResponse(response);
            this.setToken(data.access_token);
            return data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async register(username, email, password) {
        try {
            const validationErrors = this.validateRegistrationInput(
                username,
                email,
                password
            );
            if (validationErrors) {
                throw new ValidationError(validationErrors);
            }

            const response = await fetch(`${this.baseUrl}/api/auth/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    username,
                    email,
                    password,
                }),
            });

            const data = await this._handleResponse(response);
            this.setToken(data.access_token);
            return data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async logout() {
        try {
            if (!this.token) return;

            await fetch(`${this.baseUrl}/api/auth/logout`, {
                method: "POST",
                headers: this.headers,
            });

            this.clearToken();
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Chat methods
    async respond(message, chatId) {
        try {
            const messageError = this.validateMessage(message);
            if (messageError) {
                throw new ValidationError({ message: messageError });
            }

            const response = await fetch(
                `${this.baseUrl}/api/chat/${chatId}/respond`,
                {
                    method: "POST",
                    headers: this.headers,
                    body: JSON.stringify({
                        message: message,
                    }),
                }
            );

            return await this._handleResponse(response);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async deleteChat(chatId) {
        try {
            const response = await fetch(`${this.baseUrl}/api/chat/${chatId}`, {
                method: "DELETE",
                headers: this.headers,
            });
            return await this._handleResponse(response);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async getChatHistory(chatId) {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/chat/${chatId}/history`,
                {
                    headers: this.headers,
                }
            );
            const result = await this._handleResponse(response);
            return result.history;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async createChat(msg) {
        try {
            const messageError = this.validateMessage(msg);
            if (messageError) {
                throw new ValidationError({ message: messageError });
            }

            const response = await fetch(`${this.baseUrl}/api/chat/new`, {
                method: "POST",
                headers: this.headers,
                body: JSON.stringify({
                    message: msg,
                }),
            });
            const result = await this._handleResponse(response);
            return result.answer;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async getChats() {
        try {
            const response = await fetch(`${this.baseUrl}/api/chats`, {
                headers: this.headers,
            });
            const result = await this._handleResponse(response);
            return result.chats;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    handleError(error) {
        console.error("API Error:", {
            name: error.name,
            message: error.message,
            status: error.status,
            details: error.details || error.errors || "No additional details",
            stack: error.stack,
        });

        if (error instanceof ValidationError) {
            // Handle validation errors differently if needed
            console.log("Validation errors:", error.errors);
        } else if (error.status === 401) {
            this.clearToken();
        }

        throw error; // Re-throw to let the caller handle it
    }

    // Helper method to check if user is authenticated
    isAuthenticated() {
        return !!this.token;
    }
}

export default Api;
